/**
 * Data Sync Service for Dashboard
 * Fetches data from backend and syncs to IndexedDB
 */

import { getServerUrl } from '../../../../client';

const DB_NAME = 'ShepherdDB';

/**
 * Fetch records from backend for a database group
 * @param {number} groupId - Group ID
 * @param {string} groupName - Group name
 * @returns {Promise<Array>}
 */
export const fetchGroupRecordsFromServer = async (groupId, groupName) => {
    try {
        const serverUrl = getServerUrl();
        const response = await fetch(`${serverUrl}/api/db-group/${groupId}/records`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        return data.records || [];
    } catch (error) {
        console.error('[DataSync] Error fetching records from server:', error);
        return [];
    }
};

/**
 * Create or ensure object store exists for a group
 * @param {number} groupId - Group ID
 * @returns {Promise<string>} Store name
 */
const ensureGroupStore = async (groupId) => {
    const storeName = `group_${groupId}_data`;
    
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            const currentVersion = db.version;
            db.close();
            
            // Check if store exists
            if (db.objectStoreNames.contains(storeName)) {
                resolve(storeName);
                return;
            }
            
            // Need to upgrade to add the store
            const upgradeRequest = indexedDB.open(DB_NAME, currentVersion + 1);
            
            upgradeRequest.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(storeName)) {
                    const store = db.createObjectStore(storeName, { 
                        keyPath: 'id',
                        autoIncrement: true 
                    });
                    // Create index for common date field
                    store.createIndex('created_at', 'created_at', { unique: false });
                }
            };
            
            upgradeRequest.onsuccess = () => {
                upgradeRequest.result.close();
                resolve(storeName);
            };
            
            upgradeRequest.onerror = () => reject(upgradeRequest.error);
        };
        
        request.onerror = () => reject(request.error);
    });
};

/**
 * Sync records to IndexedDB
 * @param {number} groupId - Group ID
 * @param {Array} records - Records to sync
 * @returns {Promise<number>} Number of records synced
 */
export const syncRecordsToIndexedDB = async (groupId, records) => {
    try {
        const storeName = await ensureGroupStore(groupId);
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME);
            
            request.onsuccess = (event) => {
                const db = event.target.result;
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);
                
                // Clear existing data
                const clearRequest = store.clear();
                
                clearRequest.onsuccess = () => {
                    let syncedCount = 0;
                    
                    // Add all records
                    records.forEach(record => {
                        const addRequest = store.add(record);
                        addRequest.onsuccess = () => syncedCount++;
                    });
                    
                    transaction.oncomplete = () => {
                        db.close();
                        console.log(`[DataSync] Synced ${syncedCount} records to ${storeName}`);
                        resolve(syncedCount);
                    };
                };
                
                transaction.onerror = () => {
                    db.close();
                    reject(transaction.error);
                };
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[DataSync] Error syncing records to IndexedDB:', error);
        return 0;
    }
};

/**
 * Get last sync time for a group
 * @param {number} groupId - Group ID
 * @returns {Promise<number|null>} Timestamp or null
 */
export const getLastSyncTime = async (groupId) => {
    try {
        const db = await new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['version_tracking'], 'readonly');
            const store = transaction.objectStore('version_tracking');
            const key = `group_${groupId}_sync`;
            const getRequest = store.get(key);
            
            getRequest.onsuccess = () => {
                db.close();
                const result = getRequest.result;
                resolve(result ? result.last_sync : null);
            };
            
            getRequest.onerror = () => {
                db.close();
                reject(getRequest.error);
            };
        });
    } catch (error) {
        console.error('[DataSync] Error getting last sync time:', error);
        return null;
    }
};

/**
 * Check if group data exists in IndexedDB
 * @param {number} groupId - Group ID
 * @returns {Promise<boolean>}
 */
export const hasGroupData = async (groupId) => {
    try {
        const storeName = `group_${groupId}_data`;
        const db = await new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        
        // Check if store exists
        if (!db.objectStoreNames.contains(storeName)) {
            db.close();
            return false;
        }
        
        // Check if store has data
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([storeName], 'readonly');
            const store = transaction.objectStore(storeName);
            const countRequest = store.count();
            
            countRequest.onsuccess = () => {
                db.close();
                resolve(countRequest.result > 0);
            };
            
            countRequest.onerror = () => {
                db.close();
                reject(countRequest.error);
            };
        });
    } catch (error) {
        console.error('[DataSync] Error checking group data:', error);
        return false;
    }
};

/**
 * Full sync operation for a group
 * @param {number} groupId - Group ID
 * @param {string} groupName - Group name
 * @returns {Promise<{success: boolean, recordCount: number, error?: string}>}
 */
export const syncGroupData = async (groupId, groupName) => {
    try {
        console.log(`[DataSync] Starting sync for group ${groupId}...`);
        
        // Fetch records from server
        const records = await fetchGroupRecordsFromServer(groupId, groupName);
        
        if (records.length === 0) {
            console.warn(`[DataSync] No records fetched for group ${groupId}`);
            return { success: true, recordCount: 0 };
        }
        
        // Sync to IndexedDB
        const syncedCount = await syncRecordsToIndexedDB(groupId, records);
        
        return { 
            success: true, 
            recordCount: syncedCount 
        };
    } catch (error) {
        console.error('[DataSync] Error syncing group data:', error);
        return { 
            success: false, 
            recordCount: 0, 
            error: error.message 
        };
    }
};
